import React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const getScoreStyle = (score) => {
  if (score === null || score === undefined) {
    return {
      bg: "bg-gray-100 text-gray-400 border border-dashed border-gray-400",
      bar: "bg-gray-300",
    };
  }
  if (score >= 80) {
    return {
      bg: "bg-green-500/15 text-green-400 border border-green-500/30",
      bar: "bg-green-500",
    };
  }
  if (score >= 60) {
    return {
      bg: "bg-blue-500/15 text-blue-400 border border-blue-500/30",
      bar: "bg-blue-500",
    };
  }
  if (score >= 40) {
    return {
      bg: "bg-amber-500/15 text-amber-400 border border-amber-500/30",
      bar: "bg-amber-500",
    };
  }
  return {
    bg: "bg-gray-500/15 text-gray-400 border border-gray-500/30",
    bar: "bg-gray-500",
  };
};

const getScoreTooltip = (score) => {
  if (score === null || score === undefined) {
    return {
      tier: "Not scored",
      range: null,
      description:
        "This pitch was submitted before smart filtering was configured. No match score is available.",
    };
  }
  if (score >= 80) {
    return {
      tier: "Strong match",
      range: "80–100",
      description:
        "This pitch closely aligns with your sector, stage, revenue, and market preferences.",
    };
  }
  if (score >= 60) {
    return {
      tier: "Good match",
      range: "60–79",
      description:
        "This pitch meets most of your key criteria with minor mismatches.",
    };
  }
  if (score >= 40) {
    return {
      tier: "Moderate match",
      range: "40–59",
      description:
        "This pitch partially matches your preferences. May still be worth reviewing.",
    };
  }
  return {
    tier: "Low match",
    range: "0–39",
    description:
      "This pitch has limited alignment with your current preferences.",
  };
};

/**
 * ScoreBadge — reusable match score badge.
 *
 * Props:
 *   score  — number (0-100) or null
 *   size   — 'sm' | 'md' | 'lg'  (default 'md')
 */
export default function ScoreBadge({ score, size = "md", className = "" }) {
  const { bg } = getScoreStyle(score);
  const { tier, range, description } = getScoreTooltip(score);
  const label = score !== null && score !== undefined ? `${score}% Match` : "--";
  const ariaLabel =
    score !== null && score !== undefined
      ? `Match score: ${score} out of 100`
      : "Not scored";

  const tooltipContent = (
    <div className="max-w-[200px] space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-sm">{tier}</span>
        {range && (
          <span className="text-[11px] text-muted-foreground">{range}</span>
        )}
      </div>
      <p className="text-xs text-muted-foreground leading-snug">{description}</p>
    </div>
  );

  if (size === "sm") {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold cursor-default ${bg} ${className}`}
              aria-label={ariaLabel}
            >
              {label}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">{tooltipContent}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (size === "lg") {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`flex flex-col items-center justify-center w-14 h-14 rounded-xl text-2xl font-bold cursor-default ${bg} ${className}`}
              aria-label={ariaLabel}
            >
              {label}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top">{tooltipContent}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Medium (default) — used in list cards
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold cursor-default ${bg} ${className}`}
            aria-label={ariaLabel}
          >
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * ScoreComponentIndicator — shows ✓ ~ ✗ for a single scoring component.
 * Only renders when the investor has set that preference (not agnostic).
 *
 * Props:
 *   label      — string (e.g. "Sector")
 *   scoreRatio — number 0-1, or null to hide
 */
export function ScoreComponentIndicator({ label, scoreRatio }) {
  if (scoreRatio === null || scoreRatio === undefined) return null;

  let icon, color;
  if (scoreRatio >= 0.9) {
    icon = "✓";
    color = "text-green-500";
  } else if (scoreRatio >= 0.4) {
    icon = "~";
    color = "text-amber-400";
  } else {
    icon = "✗";
    color = "text-red-400";
  }

  return (
    <span
      className="text-xs text-white/50"
      aria-label={`${label}: ${scoreRatio >= 0.9 ? "full match" : scoreRatio >= 0.4 ? "partial match" : "no match"}`}
    >
      {label}{" "}
      <span className={`font-bold ${color}`}>{icon}</span>
    </span>
  );
}
